import rawFcJs from '@fanconnect/neo-api/dist/fc.js?raw';
import renderHtml from 'dom-serializer';
import type { Node } from 'domhandler';
import { Element } from 'domhandler';
import { appendChild, findOne, isTag, prepend } from 'domutils';
import { parseDocument } from 'htmlparser2';
import type JSZip from 'jszip';
import { getType } from 'mime';
import { assert } from '~/lib/assert';

/**
 * Serving a template zip means unzipping it, injecting the FC runtime into
 * `index.html`, and writing every entry into the cache under
 * `/template-versions/<id>/<path>` so relative asset references resolve.
 *
 * This lives outside the service worker because the upload dialog does the same
 * thing for a zip that hasn't been uploaded yet: it populates the cache under a
 * synthetic id and lets the service worker serve the preview from there.
 */

export const TEMPLATE_VERSIONS_CACHE = 'template-versions';

/** Matches `/template-versions/<numeric id>/<path>`. */
export const TEMPLATE_PATH_REGEX = /(\/template-versions\/\d+\/)(.*)$/;

/** The one entry a template zip must have, and the only one we modify. */
export const INDEX_HTML = 'index.html';

const tagName =
  (tag = '*') =>
  (elem: Element) =>
    isTag(elem) && elem.name === tag;

const createElement = (html: string) => {
  const nodes = parseDocument(html).children;
  assert(nodes.length === 1, 'HTML contained more than one node');
  const element = nodes[0];
  assert(element instanceof Element);
  return element;
};

const addFirstChild = (parent: Element, child: Node) => {
  if (parent.children.length) {
    prepend(parent.children[0], child);
  } else {
    appendChild(parent, child);
  }
};

const htmlElement = (dom: Node[]) => {
  const element = findOne(tagName('html'), dom);
  assert(element !== null, 'HTML document missing <html> tag');
  return element;
};

const headElement = (dom: Node[]) => {
  const html = htmlElement(dom);
  const element = findOne(tagName('head'), [html]);
  if (element !== null) return element;

  const head = createElement('<head></head>');
  addFirstChild(html, head);
  return head;
};

const updateDOM = (dom: Node[]) => {
  const script = createElement(`<script type="text/javascript">${rawFcJs}</script>`);
  addFirstChild(headElement(dom), script);
  return renderHtml(dom);
};

/** Returns the template's `index.html` with the FC runtime prepended to `<head>`. */
export const injectFcRuntime = (data: ArrayBuffer) => {
  const decoder = new TextDecoder();
  const html = decoder.decode(data);

  const document = parseDocument(html);

  const encoder = new TextEncoder();
  return encoder.encode(updateDOM(document.children));
};

export interface CacheTemplateZipOptions {
  /** Absolute URL of the template's `index.html`, with no query or hash. */
  readonly baseKey: string;
  readonly cache: Cache;
  /** Called with each `cache.put` promise, for service worker lifetime extension. */
  readonly onPut?: (promise: Promise<void>) => void;
  readonly zip: JSZip;
}

/**
 * Writes every file in `zip` into `cache`, keyed off `baseKey`. Returns the
 * response for `index.html`, or undefined if the zip has no `index.html` at its
 * root, along with the cache keys written so a caller can clean them up.
 */
export const cacheTemplateZip = async ({
  baseKey,
  cache,
  onPut,
  zip,
}: CacheTemplateZipOptions): Promise<{ keys: string[]; match: Response | undefined }> => {
  const keys: string[] = [];
  let match: Response | undefined;

  await Promise.all(
    Object.keys(zip.files).map(async (path): Promise<void> => {
      const file = zip.files[path];
      if (file.dir) return undefined;

      const location = baseKey.replace(TEMPLATE_PATH_REGEX, `$1${path}`);

      const buffer = await file.async('arraybuffer');
      const data = path === INDEX_HTML ? injectFcRuntime(buffer) : buffer;

      const response = new Response(data, {
        headers: {
          'Content-Length': data.byteLength.toString(10),
          'Content-Type': getType(path) ?? 'application/octet-stream',
        },
      });

      keys.push(location);

      // Hand the cache a clone when we also need to return the response.
      const isMatch = baseKey === location;
      if (isMatch) match = response;

      const promise = cache.put(location, isMatch ? response.clone() : response);
      onPut?.(promise);

      return await promise;
    }),
  );

  return { keys, match };
};
